import { supabase } from '../lib/supabase';
import type { RoomTheme } from '../types';

export interface Booth {
  id: string;
  slug: string;
  name: string;
  description: string;
  venueId: string;
  theme: RoomTheme;
  maxDJSlots: 1;
  status: 'live' | 'soon' | 'ended';
  createdAt: number;
  updatedAt: number;
}

interface BoothRow {
  id: string;
  slug: string;
  name: string;
  description: string;
  venue_id: string;
  theme: RoomTheme;
  max_dj_slots: number;
  status: 'live' | 'soon' | 'ended';
  created_at: number;
  updated_at: number;
}

function rowToBooth(r: BoothRow): Booth {
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    description: r.description,
    venueId: r.venue_id,
    theme: r.theme,
    maxDJSlots: 1,
    status: r.status,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'booth';
}

async function uniqueSlug(base: string, excludeId?: string): Promise<string> {
  const all = await BoothStore.list();
  const taken = new Set(
    all.filter((b) => b.id !== excludeId).map((b) => b.slug),
  );
  if (!taken.has(base)) return base;
  for (let i = 2; i < 100; i++) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now().toString(36)}`;
}

export const BoothStore = {
  async get(boothId: string): Promise<Booth | null> {
    if (!supabase) return null;
    const { data } = await supabase
      .from('booths')
      .select('*')
      .eq('id', boothId)
      .single();
    return data ? rowToBooth(data as BoothRow) : null;
  },

  async findBySlug(slug: string): Promise<Booth | null> {
    if (!supabase) return null;
    const { data } = await supabase
      .from('booths')
      .select('*')
      .eq('slug', slug)
      .single();
    return data ? rowToBooth(data as BoothRow) : null;
  },

  async list(): Promise<Booth[]> {
    if (!supabase) return [];
    const { data } = await supabase
      .from('booths')
      .select('*')
      .order('created_at', { ascending: false });
    return (data ?? []).map((r) => rowToBooth(r as BoothRow));
  },

  async listByVenue(venueId: string): Promise<Booth[]> {
    if (!supabase) return [];
    const { data } = await supabase
      .from('booths')
      .select('*')
      .eq('venue_id', venueId)
      .order('created_at', { ascending: true });
    return (data ?? []).map((r) => rowToBooth(r as BoothRow));
  },

  async create(opts: {
    name: string;
    description: string;
    venueId: string;
    theme: RoomTheme;
    status?: 'live' | 'soon' | 'ended';
  }): Promise<Booth | null> {
    if (!supabase) return null;
    const id = `booth_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const slug = await uniqueSlug(slugify(opts.name));
    const now = Date.now();
    const row: BoothRow = {
      id,
      slug,
      name: opts.name,
      description: opts.description,
      venue_id: opts.venueId,
      theme: opts.theme,
      max_dj_slots: 3,
      status: opts.status ?? 'live',
      created_at: now,
      updated_at: now,
    };
    const { data, error } = await supabase
      .from('booths')
      .insert(row)
      .select()
      .single();
    if (error) { console.error('[BoothStore] create failed', error); return null; }
    return data ? rowToBooth(data as BoothRow) : null;
  },

  async update(
    boothId: string,
    patch: Partial<Pick<Booth, 'name' | 'description' | 'venueId' | 'theme' | 'status'>>,
  ): Promise<Booth | null> {
    if (!supabase) return null;
    const updates: Record<string, unknown> = { updated_at: Date.now() };
    if (patch.name !== undefined) {
      updates.name = patch.name;
      updates.slug = await uniqueSlug(slugify(patch.name), boothId);
    }
    if (patch.description !== undefined) updates.description = patch.description;
    if (patch.venueId !== undefined) updates.venue_id = patch.venueId;
    if (patch.theme !== undefined) updates.theme = patch.theme;
    if (patch.status !== undefined) updates.status = patch.status;
    const { data, error } = await supabase
      .from('booths')
      .update(updates)
      .eq('id', boothId)
      .select()
      .single();
    if (error) { console.error('[BoothStore] update failed', error); return null; }
    return data ? rowToBooth(data as BoothRow) : null;
  },

  async remove(boothId: string): Promise<void> {
    if (!supabase) return;
    await supabase.from('booths').delete().eq('id', boothId);
  },

  async count(): Promise<number> {
    if (!supabase) return 0;
    const { count } = await supabase
      .from('booths')
      .select('*', { count: 'exact', head: true });
    return count ?? 0;
  },
};
